import React, { useState, useEffect, useRef } from "react";
import { FaTimes, FaCoins, FaPercent, FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import { formatSumma } from "../utils/format";
import CONFIG from "../config";
import "./CoursePurchaseModal.css";

export default function CoursePurchaseModal({
    isOpen,
    onClose,
    onConfirm,
    courseName,
    price,
    kursId,
    isProcessing
}) {
    const [agreed, setAgreed] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [hasReadToBottom, setHasReadToBottom] = useState(false);
    const [promoCode, setPromoCode] = useState("");
    const [promoLoading, setPromoLoading] = useState(false);
    const [promoError, setPromoError] = useState("");
    const [discountData, setDiscountData] = useState(null);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setIsClosing(false);
            setAgreed(false);
            setHasReadToBottom(false);
            setPromoCode("");
            setPromoError("");
            setDiscountData(null);

            // Check if content is already small enough to not need scrolling
            setTimeout(() => {
                if (scrollRef.current) {
                    const { scrollHeight, clientHeight } = scrollRef.current;
                    if (scrollHeight <= clientHeight + 10) {
                        setHasReadToBottom(true);
                    }
                }
            }, 500);
        }
    }, [isOpen]);

    const handleScroll = () => {
        if (!scrollRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;

        // Increased tolerance (30px) to ensure it works on all devices/browsers
        if (scrollTop + clientHeight >= scrollHeight - 30) {
            setHasReadToBottom(true);
        }
    };

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            onClose();
            setIsClosing(false);
        }, 300);
    };

    const handlePromoChange = async (e) => {
        const value = e.target.value.toUpperCase();
        setPromoCode(value);
        setPromoError("");
        setDiscountData(null);

        if (value.length >= 4) {
            setPromoLoading(true);
            try {
                const token = localStorage.getItem("loyiha_token");
                const res = await fetch(`${CONFIG.API_BASE_URL}/check_promo.php`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ code: value, kurs_id: kursId })
                }).then(r => r.json());

                if (res.status === 'success') {
                    // Map backend keys to frontend state
                    const discountPercent = res.data.chegirma_foizi || 0;
                    const discountAmount = res.data.chegirma_summasi || 0;

                    let finalPrice = price;
                    let calculatedDiscount = 0;

                    if (discountPercent > 0) {
                        calculatedDiscount = (price * discountPercent) / 100;
                        finalPrice = price - calculatedDiscount;
                    } else if (discountAmount > 0) {
                        calculatedDiscount = discountAmount;
                        finalPrice = Math.max(0, price - discountAmount);
                    }

                    setDiscountData({
                        promo_kod: value,
                        discount_percent: discountPercent,
                        discount: calculatedDiscount, // Amount deducted
                        new_price: finalPrice
                    });
                } else {
                    // Only show error if it's NOT "not found"
                    if (res.message !== "Promokod mavjud emas") {
                        setPromoError(res.message);
                    }
                }
            } catch (err) {
                setPromoError("Tanitib bo'lmadi");
            } finally {
                setPromoLoading(false);
            }
        }
    };

    if (!isOpen && !isClosing) return null;

    const handleConfirm = () => {
        if (agreed && !isProcessing) {
            onConfirm(discountData ? discountData.promo_kod : "");
        }
    };

    return (
        <div className={`purchase-modal-overlay ${isClosing ? 'modal-overlay-fade-out' : 'modal-overlay-fade-in'}`}>
            <div className={`purchase-modal-card ${isClosing ? 'modal-content-pop-out' : 'modal-content-pop-in'}`}>
                <button className="purchase-modal-close" onClick={handleClose}>
                    <FaTimes />
                </button>

                <div className="purchase-modal-header">
                    <h2>📘 SUVORIY TA’LIM PLATFORMASI</h2>
                    <h3>ONLAYN KURS FOYDALANISH SHARTLARI VA MAXFIYLIK SIYOSATI</h3>
                </div>

                <div className="purchase-modal-content">
                    {/* Moved ref and onScroll to terms-section as it has overflow-y: auto */}
                    <div className="terms-section" ref={scrollRef} onScroll={handleScroll}>
                        <p className="intro-text">
                            Hurmatli foydalanuvchi! Suvoriy ta’lim platformasida <strong>{courseName}</strong> kursini sotib olish va undan foydalanishdan oldin quyidagi <strong>onlayn kelishuv shartlari</strong>, <strong>foydalanish qoidalari</strong> va <strong>maxfiylik siyosati</strong> bilan to‘liq tanishib chiqishingiz so‘raladi. Kursni sotib olish orqali siz ushbu shartlarga <strong>rozilik bildirgan hisoblanasiz</strong>.
                        </p>

                        <div className="terms-scroll">
                            <section>
                                <h4>👤 1. TOMONLAR</h4>
                                <ul>
                                    <li><strong>Platforma egasi:</strong> Suvoriy ta’lim platformasi</li>
                                    <li><strong>Foydalanuvchi:</strong> Platformada ro‘yxatdan o‘tgan va kurs sotib olgan jismoniy shaxs</li>
                                </ul>
                            </section>

                            <section>
                                <h4>🎓 2. ONLAYN KURSDAN FOYDALANISH SHARTLARI</h4>
                                <p>Ushbu bo‘lim Suvoriy ta’lim platformasida sotib olingan kurslardan qanday tartibda va qaysi cheklovlar asosida foydalanish mumkinligini tushuntiradi.</p>

                                <h5>📌 2.1. Kursga kirish huquqi</h5>
                                <ul>
                                    <li>Sotib olingan kurs <strong>faqat bitta foydalanuvchi</strong> uchun mo‘ljallangan</li>
                                    <li>Har bir foydalanuvchiga kursdan foydalanish uchun <strong>maksimal 2 ta qurilma</strong> (masalan: telefon + noutbuk yoki kompyuter + planshet) biriktiriladi</li>
                                    <li>Biriktirilgan 2 ta qurilmadan tashqari boshqa qurilmadan kirishga urinish <strong>avtomatik bloklanadi</strong></li>
                                    <li>Login va parolni uchinchi shaxslarga berish <strong>qat’iyan taqiqlanadi</strong></li>
                                    <li>Kursga kirish faqat Suvoriy platformasining rasmiy veb interfeysi orqali amalga oshiriladi</li>
                                </ul>

                                <h5>📌 2.2. Mualliflik huquqi</h5>
                                <ul>
                                    <li>Barcha video, audio, matn, test va grafik materiallar <strong>Suvoriy platformasiga tegishli</strong></li>
                                    <li>Kurs materiallarini: 📵 ko‘chirib olish, 📤 tarqatish, 📸 yozib olish, 💾 boshqa platformaga joylash <strong>qat’iyan man etiladi</strong></li>
                                </ul>

                                <h5>📌 2.3. Foydalanish cheklovlari</h5>
                                <ul>
                                    <li>Kurs faqat <strong>ta’lim olish maqsadida</strong> foydalaniladi</li>
                                    <li>Tijoriy yoki noqonuniy maqsadlarda foydalanish taqiqlanadi</li>
                                </ul>
                            </section>

                            <section>
                                <h4>⏳ 3. KURS MUDDATI VA AMAL QILISH DAVRI</h4>
                                <ul>
                                    <li>Har bir sotib olingan kurs foydalanuvchiga <strong>2 (ikki) yil muddatga</strong> taqdim etiladi</li>
                                    <li>Kurs muddati <strong>to‘lov muvaffaqiyatli amalga oshirilgan va tizim tomonidan tasdiqlangan kundan</strong> boshlab hisoblanadi</li>
                                    <li>Ushbu amal qilish davrida foydalanuvchi kurs materiallaridan platforma qoidalariga muvofiq foydalanish huquqiga ega bo‘ladi</li>
                                    <li>Belgilangan 2 yillik muddat tugagach, kursga kirish huquqi avtomatik tarzda bekor qilinadi</li>
                                    <li>Kurs muddati uzaytirilishi faqat platforma tomonidan belgilangan tartibda va qo‘shimcha to‘lov asosida amalga oshirilishi mumkin</li>
                                </ul>
                            </section>

                            <section>
                                <h4>💳 4. TO‘LOV, HISOB-KITOB VA TANGA TIZIMI</h4>
                                <h5>💰 4.1. To‘lovlarni amalga oshirish tartibi</h5>
                                <ul>
                                    <li>Kurs uchun to‘lovlar platformada mavjud bo‘lgan rasmiy onlayn to‘lov vositalari orqali amalga oshiriladi</li>
                                    <li>To‘lov amalga oshirilganidan so‘ng, ushbu mablag‘ platformaning ichki ma’lumotlar bazasida qayd etiladi va <strong>faqat to‘lov tangaga aylantirilgandan keyin</strong> foydalanuvchi hisobidagi tanga maydonida ko‘rsatiladi</li>
                                    <li>Kurs huquqi va foydalanish imtiyozi <strong>faqat tangaga aylantirishdan keyin</strong> platforma tomonidan beriladi</li>
                                </ul>

                                <h5>🪙 4.2. Tanga (ichki hisob birligi) tizimi</h5>
                                <ul>
                                    <li>Suvoriy ta’lim platformasida ichki hisob-kitob birligi sifatida <strong>“tanga”</strong> tizimi joriy etilgan</li>
                                    <li>Foydalanuvchi tomonidan amalga oshirilgan real puldagi to‘lovlar platforma tizimi tomonidan <strong>avtomatik ravishda tangaga konvertatsiya qilinadi</strong></li>
                                    <li>Tangalar faqat Suvoriy platformasi doirasida kurslar va xizmatlar uchun ishlatiladi hamda <strong>naqd pulga yoki boshqa moliyaviy vositalarga almashtirilmaydi</strong></li>
                                </ul>

                                <h5>📊 4.3. Platforma tomonidan yuritish va nazorat</h5>
                                <ul>
                                    <li>Kurs va tanga hisoblari, amalga oshirilgan to‘lovlar va foydalanuvchi huquqlari <strong>doim platforma tomonidan yuritiladi</strong></li>
                                    <li>Texnik nosozlik yoki hisob-kitobdagi xatoliklar aniqlangan taqdirda platforma ichki tekshiruv o‘tkazish huquqini o‘zida saqlaydi</li>
                                </ul>

                                <h5>🎯 4.4. Chegirmalar va bonus siyosati</h5>
                                <ul>
                                    <li>Platformada chegirmalar, aksiyalar va bonuslar avtomatik yoki qo‘lda belgilanishi mumkin</li>
                                    <li>Chegirma va bonuslar vaqtinchalik bo‘lib, platforma ularni istalgan vaqtda bekor qilish yoki o‘zgartirish huquqiga ega</li>
                                </ul>

                                <h5>❌ 4.5. To‘lovni qaytarish shartlari</h5>
                                <ul>
                                    <li>To‘lov amalga oshirilgan va kurs uchun tanga hisobga o‘tkazilganidan keyin <strong>to‘lov qaytarilmaydi</strong></li>
                                    <li>Tangaga o‘tkazilgan mablag‘lar ham qaytarilmaydi va boshqa shaxslarga o‘tkazib berilmaydi</li>
                                </ul>
                            </section>

                            <section>
                                <h4>🔐 5. MAXFIYLIK SIYOSATI</h4>
                                <ul>
                                    <li><strong>5.1. Yig‘iladigan ma’lumotlar:</strong> Ism, telefon raqam, e-pochta, to‘lov ma’lumotlari</li>
                                    <li><strong>5.2. Ma’lumotlardan foydalanish:</strong> Aloqa qilish, kursga kirishni ta’minlash, xavfsizlik</li>
                                    <li><strong>5.3. Ma’lumotlarni himoyalash:</strong> Shaxsiy ma’lumotlar uchinchi shaxslarga <strong>berilmaydi</strong></li>
                                </ul>
                            </section>

                            <section>
                                <h4>🔒 6. XAVFSIZLIK VA NAZORAT</h4>
                                <ul>
                                    <li>Bir akkauntdan ruxsat etilmagan ko‘p qurilmada foydalanish aniqlansa: ⚠️ vaqtinchalik cheklov qo‘yiladi, 🚫 takroriy holatda akkaunt doimiy bloklanadi</li>
                                    <li>Platforma video va materiallar ustida himoya texnologiyalaridan foydalanadi</li>
                                </ul>
                            </section>

                            <section>
                                <h4>⚠️ 7. JAVOBGARLIKNI CHEKLASH</h4>
                                <ul>
                                    <li>Texnik xizmatlar sababli qisqa muddatli uzilishlar bo‘lishi mumkin</li>
                                    <li>Internet tezligi yoki foydalanuvchi qurilmasi nosozligi uchun platforma javobgar emas</li>
                                    <li>Foydalanuvchi o‘z akkaunti xavfsizligi uchun shaxsan mas’ul</li>
                                </ul>
                            </section>

                            <section>
                                <h4>📝 8. SHARTLARGA O‘ZGARTIRISH KIRITISH</h4>
                                <ul>
                                    <li>Suvoriy ta'lim platformasi ushbu foydalanish shartlarini qonunchilik yoki texnik ehtiyojlardan kelib chiqib yangilashi mumkin</li>
                                    <li>O'zgartirilgan shartlar platformada e'lon qilingan kundan boshlab kuchga kiradi</li>
                                    <li>Foydalanuvchi platformadan foydalanishda davom etishi orqali yangilangan shartlarga rozilik bildirgan hisoblanasiz</li>
                                </ul>
                            </section>

                            <section>
                                <h4>🎖 9. KURSNI YAKUNLASH VA SERTIFIKAT BERILISHI</h4>
                                <ul>
                                    <li>Foydalanuvchi kursni to‘liq yakunlaganidan so‘ng, platforma tomonidan <strong>rasmiy sertifikat</strong> beriladi</li>
                                    <li>Sertifikat kursning muvaffaqiyatli tugatilganini tasdiqlaydi va foydalanuvchi shaxsiy kabinetida saqlanadi</li>
                                </ul>
                            </section>

                            <section>
                                <h4>✅ 10. ROZILIK</h4>
                                <p>☑️ “Kurs sotib olish” tugmasini bosish orqali foydalanuvchi:</p>
                                <ul>
                                    <li>Kursdan foydalanish shartlari, jumladan 2 ta qurilmadan foydalanish va 2 yillik muddat</li>
                                    <li>To‘lovni tangaga aylantirish va kurs huquqi berilish tartibi</li>
                                    <li>Maxfiylik siyosati</li>
                                    <li>Xavfsizlik va nazorat qoidalari</li>
                                    <li>Chegirma va bonus siyosati</li>
                                    <li>To‘lovni qaytarish shartlari</li>
                                    <li>Sertifikat berilish tartibi</li>
                                </ul>
                                <p>bilan <strong>to‘liq tanishganini, tushunganini va ularga roziligini</strong> tasdiqlaydi.</p>
                            </section>

                            <div className="terms-contact">
                                <p>📞 <strong>Aloqa:</strong> Agar savollaringiz bo‘lsa, platforma orqali biz bilan bog‘lanishingiz mumkin.</p>
                                <p style={{ fontWeight: 800 }}>Suvoriy ta’lim platformasi</p>
                            </div>
                        </div>
                    </div>

                    <div className="agreement-section">
                        <label className={`agreement-checkbox ${!hasReadToBottom ? 'locked' : ''}`}>
                            <input
                                type="checkbox"
                                checked={agreed}
                                onChange={(e) => setAgreed(e.target.checked)}
                                disabled={!hasReadToBottom}
                            />
                            <span className="checkmark"></span>
                            <span className="agreement-text">
                                {hasReadToBottom ? (
                                    <span>Men <strong>kelishuv shartlariga</strong> va <strong>maxfiylik siyosatiga</strong> roziman</span>
                                ) : (
                                    <span className="scroll-warning">Shartnoma matnini oxirigacha aylantirib o'qib chiqing! 👇</span>
                                )}
                            </span>
                        </label>
                    </div>

                    {agreed && (
                        <div className="promo-section">
                            <div className="promo-input-wrapper">
                                <div className="promo-type-icon">
                                    {discountData ? (
                                        discountData.discount_percent > 0 ? <FaPercent /> : <FaCoins />
                                    ) : <FaPercent />}
                                </div>
                                <input
                                    type="text"
                                    placeholder="Promokod (ixtiyoriy)"
                                    value={promoCode}
                                    onChange={handlePromoChange}
                                    disabled={isProcessing}
                                    className={`${promoError ? 'has-error' : discountData ? 'has-success' : ''} with-icon`}
                                />
                                {promoLoading && <div className="promo-spinner"></div>}
                                {!promoLoading && discountData && <FaCheckCircle className="promo-valid-icon" />}
                                {!promoLoading && promoError && <FaExclamationCircle className="promo-invalid-icon" />}
                            </div>
                            {promoError && <p className="promo-error-msg">{promoError}</p>}
                            {discountData && (
                                <p className="promo-success-msg">
                                    {discountData.discount_percent > 0 ? <FaPercent /> : <FaCoins />}
                                    {discountData.discount_percent > 0
                                        ? `${discountData.discount_percent}% chegirma qo'llanildi!`
                                        : `${formatSumma(discountData.discount)} tanga chegirma qo'llanildi!`}
                                </p>
                            )}
                        </div>
                    )}

                    <div className="purchase-modal-footer">
                        <button className="cancel-btn" onClick={handleClose}>
                            Bekor qilish
                        </button>
                        <div className="tooltip-container" style={{ width: 'auto' }}>
                            <button
                                type="button"
                                className={`purchase-btn ${(!agreed || isProcessing) ? 'disabled' : ''}`}
                                onClick={handleConfirm}
                                disabled={!agreed || isProcessing}
                            >
                                <FaCoins />
                                <span>
                                    {isProcessing ? "Yuklanmoqda..." : (
                                        discountData ? (
                                            <>
                                                <span className="old-price">{formatSumma(price)}</span>
                                                {` Sotib olish (${formatSumma(discountData.new_price)} tanga)`}
                                            </>
                                        ) : `Sotib olish (${formatSumma(price)} tanga)`
                                    )}
                                </span>
                            </button>
                            {discountData && !isProcessing && (
                                <span className="payment-tooltip">
                                    <FaCoins style={{ marginRight: '5px', color: '#f1c40f' }} />
                                    Asl narxi: {formatSumma(price)} tanga <br />
                                    {discountData.discount_percent > 0 ? (
                                        <>
                                            <FaPercent style={{ marginRight: '5px', color: '#2ecc71' }} />
                                            Chegirma: {discountData.discount_percent}%
                                        </>
                                    ) : (
                                        <>
                                            <FaCoins style={{ marginRight: '5px', color: '#2ecc71' }} />
                                            Chegirma: {formatSumma(discountData.discount)} tanga
                                        </>
                                    )}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
